// src/pages/FAQ.js

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Shield, CreditCard, Video, Clock, HelpCircle, Globe, Bell, Calendar, Cpu } from 'lucide-react';
import '../components/css/FAQ.css';

// (Optional) Make sure you have an image for the "virtual audition" card in your assets
import virtualAuditionImg from '../assets/img/virtual-audition.png';

const allFaqData = {
  General: [
    {
      q: "Are all casting calls verified?",
      a: "Yes. Every audition posted on BookMyAudition undergoes a rigorous verification process by our security team to ensure legitimacy and actor safety.",
      icon: <Shield size={20} />,
      fullWidth: false, 
    },
    {
      q: "What if I encounter a problem?",
      a: "Our support team is here to help! You can reach out to us via the 'Contact Us' page or email us at support@bookmyaudition.com. We typically respond within 24 hours.",
      icon: <HelpCircle size={20} />,
      fullWidth: false,
    },
    {
      q: "How do virtual auditions work?",
      a: "Virtual auditions happen via integrated Google Meet links. Once you book a slot, you'll receive an invite. At the scheduled time, just click the link in your dashboard to join the live audition.",
      icon: <Video size={20} />,
      fullWidth: true,
      image: virtualAuditionImg,
    },
  ],
  Account: [
    {
      q: "How do I create an account?",
      a: "Simply click the 'Sign Up' button at the top right, then select 'Register'. You'll need to provide your professional headshot, resume, and contact details to begin your journey.",
      icon: <User size={20} />,
      fullWidth: false,
    },
    {
      q: "How do I upload my portfolio?",
      a: "Go to your 'Profile' section. There you can upload high-resolution headshots, add links to your showreels (YouTube/Vimeo), and fill in your physical attributes, experience, and skills.",
      icon: <User size={20} />,
      fullWidth: false,
    },
    {
      q: "How do I get notified about new roles?",
      a: "You can enable 'Role Alerts' in your settings. Based on your profile and preferences, we will send you real-time push notifications and emails as soon as a matching role is posted.",
      icon: <Bell size={20} />,
      fullWidth: false,
    },
    {
      q: "Can I apply to roles in different countries?",
      a: "Absolutely! BookMyAudition has a global reach. You can use the location filter in 'Browse Roles' to find opportunities in any city or country worldwide.",
      icon: <Globe size={20} />,
      fullWidth: false,
    },
  ],
  Payments: [
    {
      q: "What payment methods are accepted?",
      a: "We accept all major credit cards, PayPal, and Apple Pay. For professional agencies, we also offer monthly invoicing and corporate billing options.",
      icon: <CreditCard size={20} />,
      fullWidth: true,
    },
  ],
  Auditions: [
    {
      q: "Can I track my application status?",
      a: "Absolutely. Your 'My Auditions' dashboard provides real-time updates—from 'Reviewing Resume' to 'Audition Booked' or 'Role Cast'.",
      icon: <Clock size={20} />,
      fullWidth: false,
    },
    {
      q: "Can I reschedule my audition?",
      a: "Yes, you can request a reschedule through your 'My Auditions' dashboard, provided the casting director has enabled this option for that specific role.",
      icon: <Calendar size={20} />,
      fullWidth: false,
    },
     {
      q: "What is AI scoring for self-tapes?",
      a: "Our AI analysis tool reviews your self-tape for technical aspects like lighting, audio clarity, and performance markers to provide a preliminary score for casting directors.",
      icon: <Cpu size={20} />,
      fullWidth: false,
    },
  ],
};

const FAQ = () => {
  const [activeTab, setActiveTab] = useState('General');
  const activeQuestions = allFaqData[activeTab];

  return (
    <div className="faq-page-modern">
      {/* --- HERO HEADER --- */}
      <section className="faq-hero">
        <h1>Frequently Asked Questions</h1>
        <p>Find answers to common questions about BookMyAudition</p>
      </section>

      {/* --- TABS --- */}
      <div className="faq-tabs-container">
        <div className="faq-tabs">
          {Object.keys(allFaqData).map((tabName) => (
            <button
              key={tabName}
              onClick={() => setActiveTab(tabName)}
              className={`faq-tab-btn ${activeTab === tabName ? 'active' : ''}`}
            >
              {tabName}
            </button>
          ))}
        </div>
      </div>

      {/* --- QUESTIONS GRID --- */}
      <motion.div className="faq-grid-container" layout>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab} // Animate the container when the tab changes
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="faq-grid-inner" // Use an inner grid for items
          >
            {activeQuestions.map((item, index) => (
              <div
                key={item.q}
                className={`faq-card ${item.fullWidth ? 'full-width' : ''}`}
              >
                <div className="faq-card-header">
                  <span className="faq-card-number">0{index + 1}</span>
                  <div className="faq-card-icon">{item.icon}</div>
                </div>
                <div className={`faq-card-body ${item.image ? 'with-image' : ''}`}>
                  {item.image && <img src={item.image} alt="" className="faq-card-image" />}
                  <div className="faq-card-text">
                      <h3 className="faq-card-title">{item.q}</h3>
                      <p className="faq-card-answer">{item.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default FAQ;